/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { AppConfigService } from '@app/common/app-config.service';
import { MicroAppService } from '@app/di/channel/microapp/micro-app.service';
import { MicroAppBase } from '@app/di/channel/microapp/micro-app-base';
import { RisRequestType, RisRequest, RisResponse, RisSupportedTab } from '@app/di/channel/window/postmessage.types';
import { UtilService } from '@app/util/util.service';
import { DiConfigService } from '@app/di/di-config.service';
import { appConstants } from '@app/common/app-constants';
import { filter, takeUntil } from 'rxjs/operators';
import { BackgroundService } from '@app/core/background-service';
import { SignOutService } from '@app/common/sign-out.service';
import { ProxyDataService } from '@app/core/http';
import { DateTimePipe } from '@app/common/pipes';
import { DataTransformationService } from '@app/common/data-transformation.service';

interface RISCVConfig {
  enabled: boolean;
  appUrl: string;
  restUrl: string;
  scanDocEnabled: boolean;
  billingEnabled: boolean;
  domain: string;
  keepAliveInterval: number;
}

interface LastResponsePromise<T> {
  promise?: Promise<T>;
  resolve?: any;
  reject?: any;
  timer?: any;
}

interface LastRequest {
  requestType: RisRequestType;
  requestPayload: RisRequest;
  response: RisResponse;
  responsePromise?: LastResponsePromise<RisResponse>;
  ackPromise?: LastResponsePromise<RisResponse>;
}

interface OpenedContexts {
  activeTab: RisSupportedTab;
}

enum ResponseTimeOutMessage {
  KEEPALIVE_ACK_TIMEOUT = 'KEEPALIVE ACKNOWLEDGEMENT TIMEOUT',
  KEEPALIVE_RESPONSE_TIMEOUT = 'KEEPALIVE RESPONSE TIMEOUT',
  REQUEST_ACK_TIMEOUT = 'REQUEST ACKNOWLEDGEMENT TIMEOUT',
  REQUEST_RESPONSE_TIMEOUT = 'REQUEST RESPONSE TIMEOUT'
}

enum ResponseCodes {
  OK = 200,
  ACKNOWLEDGEMENT = 202,
  BAD_REQUEST = 400
}

const log = new Logger('RISCVService');

@Injectable({
  providedIn: 'root'
})
export class RISCVService {
  private readonly constants = {
    risiContentViewer: 'risiContentViewer',
    scanneddocuments: 'scanneddocuments',
    accountinginfo: 'accountinginfo'
  };
  private config: RISCVConfig = {
    enabled: false,
    appUrl: '',
    restUrl: '',
    scanDocEnabled: false,
    billingEnabled: false,
    domain: '',
    keepAliveInterval: 30000
  };
  private appInstance: MicroAppBase<RisResponse> | null = null;
  private keepAliveTimerClose: ((reason?: string) => void) | null = null;
  private keepAliveLastRequest: LastRequest = null;
  private lastRequest: LastRequest = null;
  private lastDefaultTab: RisSupportedTab | null = null;
  private userInitialized = false;
  private openedContexts = new Map<number, OpenedContexts>();
  private tabsEnabled: RisSupportedTab[] = [];
  private cleanupInProgress = false;

  constructor(
    private readonly appConfigService: AppConfigService,
    private readonly microappService: MicroAppService,
    private readonly diConfigService: DiConfigService,
    private readonly backgroundService: BackgroundService,
    private readonly signOutService: SignOutService,
    private readonly dataService: ProxyDataService,
    private readonly dateTimePipe: DateTimePipe,
    private readonly dataTransformationService: DataTransformationService
  ) {}

  // #region Initialization
  init() {
    const configString = this.appConfigService.getAppConfigByKey(this.constants.risiContentViewer);
    if (!configString) {
      log.warn('No config found, using defaults.');
    } else {
      try {
        log.debug('Received config : ', configString);
        const parsed = JSON.parse(configString);
        if (this.isValidConfig(parsed)) {
          this.config = { ...this.config, ...parsed };
          this.tabsEnabled = [
            (this.config.scanDocEnabled && this.constants.scanneddocuments) || undefined,
            (this.config.billingEnabled && this.constants.accountinginfo) || undefined
          ].filter(Boolean) as RisSupportedTab[];
          this.signOutService.registerCallback(this.cleanup.bind(this));
        }
      } catch (e) {
        log.error('Configuration initialization failed', e);
      }
    }
    log.debug('Configuration initialized', this.config);
  }

  private isValidConfig(obj: any): obj is RISCVConfig {
    const isString = (v: any) => typeof v === 'string';
    const isBoolean = (v: any) => typeof v === 'boolean';
    const isValidUrl = (v: string) => {
      try {
        if (v === '') return false;
        new URL(v);
        return true;
      } catch {
        return false;
      }
    };

    const errors: string[] = [];
    if (obj && typeof obj === 'object') {
      if (!isString(obj.appUrl) || !isValidUrl(obj.appUrl)) {
        errors.push('appUrl:' + obj.appUrl);
      }
      if (!isBoolean(obj.scanDocEnabled)) {
        errors.push('scanDocEnabled:' + obj.scanDocEnabled);
      }
      if (!isBoolean(obj.billingEnabled)) {
        errors.push('billingEnabled:' + obj.billingEnabled);
      }
      if (!isString(obj.domain) || obj.domain === '' || !this.appConfigService.getDomainSysCode(obj.domain)) {
        errors.push('domain:' + obj.domain);
      }
      if (obj.scanDocEnabled) {
        if (!isString(obj.restUrl) || !isValidUrl(obj.restUrl)) {
          errors.push('restUrl:' + obj.restUrl);
        }
      }
    }
    if (errors.length > 0) {
      log.error('RISi Content Viewer config validation failed for :' + errors.join(', ') + '; Please reset');
      return false;
    }
    return true;
  }
  // #endregion Initialization

  // #region Getters
  isScanDocEnabled(): boolean {
    return this.config.scanDocEnabled;
  }
  isBillingEnabled(): boolean {
    return this.config.billingEnabled;
  }
  getLastDefaultTab(): RisSupportedTab | null {
    return this.lastDefaultTab;
  }
  // #endregion Getters

  private extractContextInfo(context: any): { accessionId: string | null; patientId: string | null } {
    const domainInfo = this.dataTransformationService.filterDomainIdentifier(
      this.config.domain,
      context.patient.identifier
    );

    if (
      domainInfo?.domainIdentifier === 'Unavailable' ||
      domainInfo?.domainIdentifier === '' ||
      context.requestedProcedure?.accessionNo === ''
    ) {
      log.warn('extractContextInfo - Accession/Patient ID missing, Verify the context.');
    }

    return {
      accessionId: context.requestedProcedure?.accessionNo || null,
      patientId: domainInfo?.domainIdentifier || null
    };
  }

  async getScanDocCount(context: any): Promise<{ hasCount: boolean; count: number }> {
    if (!this.config.scanDocEnabled) {
      return { hasCount: false, count: 0 };
    }
    try {
      const { accessionId, patientId } = this.extractContextInfo(context);
      const response = await this.dataService.httpGet(
        appConstants.appURLs.riscvScanDocCount + '?accessionId=' + accessionId + '&patientId=' + patientId
      );
      log.debug('getScanDocCount - success with response :', response);
      return { hasCount: response?.scannedDocumentCount > 0, count: response?.scannedDocumentCount || 0 };
    } catch (error) {
      log.error('getScanDocCount - failed, details :', error);
      throw error;
    }
  }

  private async launch() {
    try {
      this.appInstance = await this.microappService.registerMicroApp<MicroAppBase<RisResponse>>(
        appConstants.APP_ID.RISI,
        this.config.appUrl,
        'Window'
      );
      this.appInstance.appReady = false;
      this.appInstance?.status
        .pipe(
          filter(() => !this.cleanupInProgress),
          takeUntil(this.appInstance['destroy$'])
        )
        .subscribe(response => this.handleResponse(response));
      await this.appInstance.waitForAppReady(this.config.keepAliveInterval);
      if (this.keepAliveTimerClose) {
        this.keepAliveTimerClose('RISI: LAUNCH');
      }
      this.keepAliveTimerClose = this.backgroundService.setAsyncInterval(
        async () => {
          await this.updateToken();
        },
        this.config.keepAliveInterval,
        { callerName: 'RISI: LAUNCH' }
      );

      return await this.sendMessage(RisRequestType.INIT_USER_CONTEXT);
    } catch {
      return false;
    }
  }

  private async updateToken() {
    const requestPayload: RisRequest = await this.buildPayload(RisRequestType.UPDATE_TOKEN);
    this.keepAliveLastRequest = {
      requestType: RisRequestType.UPDATE_TOKEN,
      requestPayload,
      response: null,
      ackPromise: {} as LastResponsePromise<RisResponse>,
      responsePromise: {} as LastResponsePromise<RisResponse>
    };
    this.keepAliveLastRequest.ackPromise = this.createPromiseWithTimeout<RisResponse>(
      appConstants.TIME.SS_FIVE,
      ResponseTimeOutMessage.KEEPALIVE_ACK_TIMEOUT
    );
    this.keepAliveLastRequest.responsePromise = this.createPromiseWithTimeout<RisResponse>(
      appConstants.TIME.SS_THIRTY,
      ResponseTimeOutMessage.KEEPALIVE_RESPONSE_TIMEOUT
    );
    this.appInstance?.sendMessage(requestPayload);
    try {
      await this.keepAliveLastRequest.ackPromise.promise;
      const response = await this.keepAliveLastRequest.responsePromise.promise;
      log.debug('Keepalive response received');
      if (response.code === 400) {
        this.cleanup();
      }
    } catch (error) {
      log.error('Keepalive error:', error);
      this.cleanup();
    }
  }

  private async buildPayload(requestType: RisRequestType, context?: any, activeTab?: RisSupportedTab) {
    const token = await this.diConfigService.getJwtToken();
    let requestPayload: RisRequest = { requestType, requestId: UtilService.getUUID(), token };
    switch (requestType) {
      case RisRequestType.INIT_USER_CONTEXT:
        requestPayload = {
          ...requestPayload,
          callingEntity: appConstants.APP_ID.WFM
        };
        break;

      case RisRequestType.OPEN:
        const { accessionId, patientId } = this.extractContextInfo(context);
        const {
          patient: { displayName, name, birthDate }
        } = context;
        requestPayload = {
          ...requestPayload,
          accessionId: accessionId,
          tabsToShow: this.tabsEnabled,
          activeTab,
          patientData: {
            patientId,
            patientName: name?.text || displayName,
            patientDOB: this.dateTimePipe.transform(birthDate, appConstants.Standard_DateFormats.YYYYMMDD_WITH_HYPHEN)
          }
        };
        break;
    }
    return requestPayload;
  }

  private shouldSkipDuplicateRequest(requestType: RisRequestType): boolean {
    return this.lastRequest?.requestType === RisRequestType.CLEAR && requestType === RisRequestType.CLEAR;
  }

  private shouldClearPendingRequest(requestType: RisRequestType): boolean {
    return requestType === RisRequestType.CLEAR || requestType === RisRequestType.CLOSE;
  }

  private async executeMessageRequest(requestPayload: RisRequest): Promise<{
    ackSuccess: boolean;
    response?: RisResponse;
  }> {
    const ackPromise = this.createAckPromise();
    const responsePromise = this.createResponsePromise();

    log.debug(`Request Payload:`, requestPayload);
    this.appInstance.sendMessage(requestPayload);

    try {
      await ackPromise;
      const response = await responsePromise;
      return { ackSuccess: true, response };
    } catch (error) {
      if (error.toString().includes('ACKNOWLEDGEMENT TIMEOUT')) {
        log.error('Acknowledgement error:', error);
        this.clearTimer(this.lastRequest?.ackPromise);
        this.clearTimer(this.lastRequest?.responsePromise);
        return { ackSuccess: false };
      }
      throw error;
    }
  }

  private processMessageResponse(
    response: RisResponse,
    requestType: RisRequestType,
    context?: any,
    activeTab?: RisSupportedTab
  ): boolean {
    this.lastRequest.response = response;
    const result = response.code === ResponseCodes.OK;

    log.debug(`Request ${result ? 'Success' : 'Failed'}`, requestType, response);
    if (!result) {
      log.warn('Last action failed', requestType);
    }

    this.handleOpenedContexts(requestType, context, activeTab);
    return result;
  }

  private async sendMessage(requestType: RisRequestType, context?: any, activeTab?: RisSupportedTab, isRetry = false) {
    try {
      if (this.shouldSkipDuplicateRequest(requestType)) {
        return true;
      }
      // For CLEAR or CLOSE requests, dismiss any pending lastRequest and proceed
      if (this.shouldClearPendingRequest(requestType)) {
        this.clearTimer(this.lastRequest?.ackPromise);
        this.clearTimer(this.lastRequest?.responsePromise);
        this.lastRequest = null;
      }

      const requestPayload = await this.buildPayload(requestType, context, activeTab);
      this.lastRequest = { requestType, requestPayload, response: null };

      const result = await this.executeMessageRequest(requestPayload);

      if (!result.ackSuccess) {
        return await this.handleAcknowledgmentFailure(requestType, context, activeTab, isRetry);
      }
      return this.processMessageResponse(result.response!, requestType, context, activeTab);
    } catch (error) {
      log.error('Timeout: For:', requestType, error);
      return false;
    }
  }

  async setContext(context: any, tab?: RisSupportedTab) {
    if (!tab && !this.appInstance?.appReady) {
      return true;
    }

    if (!this.appInstance?.appReady || !this.userInitialized) {
      this.userInitialized = await this.launch();
      if (!this.userInitialized) {
        log.error('Failed to launch RISI: Application not ready or User not initialized');
        this.cleanup();
        return false;
      }
    }

    const targetTab = tab || this.openedContexts.get(context?.states?.workitemId)?.activeTab || this.lastDefaultTab;
    this.lastDefaultTab = targetTab;
    const response = await this.sendMessage(RisRequestType.OPEN, context, targetTab);
    if (response && tab) {
      setTimeout(() => {
        try {
          this.appInstance.restore();
        } catch (error) {
          log.debug('Failed to focus the window', error);
        }
      });
    }
    return response;
  }

  async clearContext(context?: any) {
    // Check if app is properly launched and has opened contexts
    if (!this.appInstance || this.openedContexts.size === 0) {
      log.debug('Skip clearing context: app not launched, no app instance, or no opened contexts');
      return false;
    }
    if (this.cleanupInProgress) {
      log.debug('Skip clearing context: cleanup in progress');
      return false;
    }

    const firstResult = await this.sendMessage(RisRequestType.CLEAR, context);
    if (firstResult) {
      return true;
    }

    log.warn('First attempt to clear context failed, retrying...');
    const retryResult = await this.sendMessage(RisRequestType.CLEAR, context);

    if (!retryResult) {
      log.error('App not responding to clear context request, closing: RISI');
      this.cleanup();
      return false;
    }

    return true;
  }

  async close() {
    if (this.keepAliveTimerClose) {
      this.keepAliveTimerClose('RISI: CLOSE');
    }
    if (!this.appInstance?.appReady) {
      log.debug(`Skipping close: App not ready or launched`);
      return true;
    }
    const result = await this.sendMessage(RisRequestType.CLOSE);
    return result;
  }

  private createPromiseWithTimeout<T>(timeoutMs: number, timeoutMessage: string): LastResponsePromise<T> {
    let resolve: (value: T) => void;
    let reject: (reason: any) => void;
    let timer: any;

    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
      timer = setTimeout(() => {
        rej(timeoutMessage);
      }, timeoutMs);
    });

    return { promise, resolve: resolve!, reject: reject!, timer };
  }

  private createAckPromise() {
    if (!this.lastRequest.ackPromise) {
      this.lastRequest = {
        ...this.lastRequest,
        ackPromise: {} as LastResponsePromise<RisResponse>
      };
    }
    this.lastRequest.ackPromise = this.createPromiseWithTimeout<RisResponse>(
      appConstants.TIME.SS_FIVE,
      'ACKNOWLEDGEMENT TIMEOUT for ' + this.lastRequest.requestType
    );
    return this.lastRequest.ackPromise.promise;
  }

  private createResponsePromise() {
    if (!this.lastRequest.responsePromise) {
      this.lastRequest = {
        ...this.lastRequest,
        responsePromise: {} as LastResponsePromise<RisResponse>
      };
    }
    this.lastRequest.responsePromise = this.createPromiseWithTimeout<RisResponse>(
      appConstants.TIME.SS_THIRTY,
      'RESPONSE TIMEOUT for ' + this.lastRequest.requestType
    );
    return this.lastRequest.responsePromise.promise;
  }

  private async handleResponse(response: RisResponse) {
    if (!response) {
      return;
    }
    log.debug(`RISI: Response: ${JSON.stringify(response)}`);
    if (response?.description === 'READY') {
      this.appInstance.appReady = true;
      return;
    }
    if ((response as any)?.closed == true && this.appInstance && !this.cleanupInProgress) {
      log.debug('App instance closed, triggering cleanup');
      this.cleanup();
      return;
    }

    if (this.keepAliveLastRequest?.requestPayload?.requestId === response.requestId) {
      if (response.code === ResponseCodes.ACKNOWLEDGEMENT) {
        this.keepAliveLastRequest?.ackPromise?.resolve(response);
        this.clearTimer(this.keepAliveLastRequest?.ackPromise);
        return;
      }
      if (response.code === ResponseCodes.OK || response.code === ResponseCodes.BAD_REQUEST) {
        this.keepAliveLastRequest?.responsePromise?.resolve(response);
        this.clearTimer(this.keepAliveLastRequest?.responsePromise);
        return;
      }
    }

    if (this.lastRequest?.requestPayload?.requestId === response.requestId) {
      if (response.code === ResponseCodes.ACKNOWLEDGEMENT) {
        this.lastRequest?.ackPromise?.resolve(response);
        this.clearTimer(this.lastRequest.ackPromise);
        return;
      }

      if (response.code === ResponseCodes.OK || response.code === ResponseCodes.BAD_REQUEST) {
        log.debug('Response received for request:', this.lastRequest.requestType);
        this.lastRequest?.responsePromise?.resolve(response);
        this.clearTimer(this.lastRequest.responsePromise);
        return;
      }
    }
  }

  private handleOpenedContexts(requestType: RisRequestType, context?: any, tab?: RisSupportedTab) {
    switch (requestType) {
      case RisRequestType.OPEN:
        if (context?.states?.workitemId) {
          this.openedContexts.set(context?.states?.workitemId, { activeTab: tab });
        }
        break;

      case RisRequestType.CLEAR:
        if (context && this.openedContexts.has(context?.id)) {
          this.openedContexts.delete(context.id);
        }
        break;

      case RisRequestType.CLOSE:
        this.openedContexts.clear();
        this.cleanup();
        break;
      case RisRequestType.INIT_USER_CONTEXT:
        this.openedContexts.clear();
        break;
    }
  }

  private clearAllTimers() {
    this.clearTimer(this.keepAliveLastRequest?.ackPromise);
    this.clearTimer(this.keepAliveLastRequest?.responsePromise);
    this.clearTimer(this.lastRequest?.responsePromise);
    this.clearTimer(this.lastRequest?.ackPromise);
  }

  private clearTimer(timerRef: LastResponsePromise<any>) {
    if (timerRef?.timer) {
      clearTimeout(timerRef.timer);
      timerRef.timer = null;
    }
  }

  private async handleAcknowledgmentFailure(
    requestType: RisRequestType,
    context?: any,
    activeTab?: RisSupportedTab,
    isRetry = false
  ): Promise<boolean> {
    if (!isRetry && (requestType === RisRequestType.OPEN || requestType === RisRequestType.CLEAR)) {
      log.warn('Acknowledgment not received for ' + requestType + ' request, attempting recovery...');
      if (requestType === RisRequestType.CLEAR) {
        this.cleanup();
        return true;
      }
      try {
        log.debug('Starting acknowledgment failure recovery process for:', requestType);

        this.cleanup();

        log.debug('Relaunching app after acknowledgment failure');
        this.userInitialized = await this.launch();
        if (!this.userInitialized) {
          log.error('Failed to relaunch app during recovery');
          return false;
        }

        log.debug('Retrying original request after successful relaunch');
        const retryResult = await this.sendMessage(requestType, context, activeTab, true);

        if (!retryResult) {
          log.error('Second attempt failed for request type:', requestType);
          this.cleanup();
          return false;
        }
        return true;
      } catch (error) {
        log.error('Error during acknowledgment failure recovery:', error);
        this.cleanup();
        return false;
      }
    }
    return false;
  }

  cleanup = () => {
    if (this.cleanupInProgress) {
      log.debug('Cleanup already in progress, skipping...');
      return;
    }

    this.cleanupInProgress = true;
    log.debug('Starting cleanup process');
    this.clearAllTimers();
    if (this.appInstance) {
      this.appInstance.close();
      this.appInstance = null;
    }
    this.openedContexts.clear();
    this.userInitialized = false;
    if (this.keepAliveTimerClose) {
      this.keepAliveTimerClose?.('RISI: CLEANUP');
    }
    this.cleanupInProgress = false;
    log.debug('Cleanup process completed');
  };
}
